import { Grid } from '@mui/material';
import { useRouter } from 'next/router';
import { type FocusEvent, useCallback, useEffect } from 'react';
import {
  type FieldValues,
  type UseFormSetValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { addressAutofillHelper } from 'src/components/AddressInput/StreetAutocomplete/addressAutofillHelper';
import { StreetAutocomplete } from 'src/components/AddressInput/StreetAutocomplete/StreetAutocomplete';
import { FormTextField } from 'src/components/Inputs/FormTextField';
import { useLicensedStateContext } from 'src/contexts/LicensedStateContext';
import type { GooglePlacesResult } from 'src/views/Coversheet/utils/googlePlaces';
import { useInteraction } from '@/common/hooks/useInteraction';
import type { InteractionType } from '@/common/types/interaction';
import { stripEmoji } from '@/common/utils/string';
import type { FormData } from '@/views/NewInteraction/types';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { StateDropdown } from './StateDropdown/StateDropdown';

interface Props {
  stateOptions: (string | undefined)[];
  onValueChange?: UseFormSetValue<FieldValues>;
  interactionId?: string;
  type: InteractionType;
  incomplete?: boolean;
  enableListingMatch?: boolean;
}

/**
 * States that do not require a County.
 */
const OPTIONAL_COUNTY_STATES = new Set(['DC']);

export const AddressInput = ({
  stateOptions,
  onValueChange,
  interactionId,
  type,
  incomplete,
  enableListingMatch,
}: Props) => {
  const { addStateError, addRedirectStateError, clearErrors } =
    useLicensedStateContext();
  const router = useRouter();
  const { trigger, setValue } = useFormContext();
  const [stateValue, representationType] = useWatch<
    FormData,
    ['state', 'representationType']
  >({
    name: ['state', 'representationType'],
  });
  const isCountyRequired = !OPTIONAL_COUNTY_STATES.has(stateValue);

  const isStateValid = useCallback(
    (state: unknown) => {
      return stateOptions.some((stateOption) => state === stateOption);
    },
    [stateOptions],
  );

  const trackEvent = useTrackInteractionsAnalytics();

  const handleAddressAutofill = useCallback(
    (autofillValue: GooglePlacesResult) => {
      trackEvent(TrackInteractionsEvent.ClickAddressAutofill, {
        eventContainer: `${type}_address_card`,
        representationType,
      });

      const autofillInfo = isStateValid(autofillValue.state)
        ? autofillValue
        : { street1: autofillValue.street1, state: autofillValue.state };

      addressAutofillHelper(autofillInfo, onValueChange);
      if (!isStateValid(autofillValue.state)) {
        addStateError(
          'The logged in user is not licensed to transact in this state.',
        );
      }
    },
    [
      addStateError,
      isStateValid,
      onValueChange,
      trackEvent,
      type,
      representationType,
    ],
  );

  useEffect(() => {
    clearErrors();

    // Revalidate county field
    void trigger('county');
  }, [clearErrors, stateValue, trigger]);

  const { data: interaction } = useInteraction(
    interactionId,
    type,
    !incomplete,
  );

  const { state, street1, street2, city, zip, county } =
    interaction?.property ?? {};

  const currentState = stateValue ?? state;

  useEffect(() => {
    if (incomplete) {
      if (!state) {
        addRedirectStateError(
          "This transaction doesn't have a complete address. Please fill out all the required fields to proceed.",
          'warning',
        );
        return;
      }
      if (!isStateValid(currentState)) {
        addRedirectStateError(
          "This transaction was set in a state where the Primary Agent doesn't have a license. Please update the information to proceed.",
          'error',
        );
      }
    }
  }, [
    addRedirectStateError,
    currentState,
    incomplete,
    isStateValid,
    onValueChange,
    router.route,
    state,
    stateValue,
  ]);

  useEffect(() => {
    if (incomplete) {
      if (state) {
        addressAutofillHelper({ street1 }, onValueChange);
      } else {
        addressAutofillHelper(
          { street1, street2, city, zip, county },
          onValueChange,
        );
      }
    }
  }, [city, county, incomplete, onValueChange, state, street1, street2, zip]);

  const handleFieldSelect = (e: FocusEvent<HTMLInputElement>) => {
    // Don't track when select box regains focus after popover menu closes
    if (e.relatedTarget?.matches('[role="option"], .MuiPaper-root')) {
      return;
    }

    const fieldName = e.target.name ?? e.target.id;

    trackEvent(TrackInteractionsEvent.SelectField, {
      eventContainer: `${type}_address_card`,
      eventTarget: `location.${fieldName}`,
      selected_input: e.target.value,
      representationType,
    });
  };

  const stripEmojiOnBlur = useCallback(
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const stripped = stripEmoji(e.target.value);
      if (stripped !== e.target.value) {
        setValue(e.target.name, stripped, { shouldValidate: true });
      }
    },
    [setValue],
  );

  return (
    <Grid
      container
      rowSpacing={1.25}
      columnSpacing={2}
      sx={{
        marginTop: 2,
      }}
    >
      <Grid size={{ xs: 12, lg: 10 }}>
        <StreetAutocomplete
          name="street1"
          label="Street"
          type={type}
          handleAutocompleteSelect={handleAddressAutofill}
          onInputBlur={stripEmojiOnBlur}
          enableListingMatch={enableListingMatch}
          required
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 2 }}>
        <FormTextField
          name="street2"
          label="Apt/Suite Number"
          fullWidth
          placeholder={street2 ?? ''}
          sx={{
            borderColor: 'grey.A200',
          }}
          slotProps={{
            inputLabel: {
              'aria-label': 'Apartment or Suite Number',
              sx: {
                color: 'grey.A200',
              },
            },
          }}
          onFocus={handleFieldSelect}
          onBlur={stripEmojiOnBlur}
          disableRequiredHighlight
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 5 }}>
        <FormTextField
          name="city"
          label="City"
          fullWidth
          required
          placeholder={city ?? ''}
          onFocus={handleFieldSelect}
          onBlur={stripEmojiOnBlur}
          disableRequiredHighlight
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 2 }}>
        <StateDropdown onFocus={handleFieldSelect} options={stateOptions} />
      </Grid>
      <Grid size={{ xs: 6, lg: 2 }}>
        <FormTextField
          name="zip"
          label="Zip Code"
          fullWidth
          required
          placeholder={zip ?? ''}
          onFocus={handleFieldSelect}
          onBlur={stripEmojiOnBlur}
          disableRequiredHighlight
          slotProps={{
            htmlInput: {
              maxLength: 10,
            },
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 3 }}>
        <FormTextField
          name="county"
          label="County"
          fullWidth
          required={isCountyRequired}
          placeholder={county ?? ''}
          onFocus={handleFieldSelect}
          onBlur={stripEmojiOnBlur}
          disableRequiredHighlight
        />
      </Grid>
    </Grid>
  );
};
